<template>
  <div>
    <div
      class="condition d-flex flex-wrap align-items-start"
      data-testid="condition"
    >
      <template v-if="disableVariable">
        <div
          data-testid="condition-variable-dropdown"
          class="variable-dropdown mb-2 mr-2 disabled-variable-text"
        >
          <span
            >{{ selectedVariable.label }}
            {{ operatorMap[selectedOperator.name] }}</span
          >
        </div>
      </template>
      <template v-else>
        <select-input
          class="variable-dropdown mb-2 mr-2"
          data-testid="condition-variable-dropdown"
          boundary="viewport"
          :disabled="disabled || disableVariable"
          :value="selectedVariable ? selectedVariable.name : null"
          :options="variableOptions"
          placeholder="Select a variable"
          @input="changeConditionVariable"
        >
          <template slot="option-content" slot-scope="{ option: { label } }">
            <div data-testid="condition-variable-dropdown-option">
              {{ label }}
            </div>
          </template>
        </select-input>
        <select-input
          class="operator-dropdown mr-2 mb-2"
          data-testid="condition-operator-dropdown"
          :disabled="disabled || !selectedVariable"
          boundary="viewport"
          :value="selectedOperator ? selectedOperator.name : null"
          :options="availableOperators || []"
          @input="changeConditionOperator"
        >
          <template slot="button-content">
            <span v-if="!selectedOperator">Select an operator</span>
            <span v-else> {{ operatorMap[selectedOperator.name] }} </span>
          </template>
          <template slot="option-content" slot-scope="{ option: { name } }">
            <div data-testid="condition-operator-dropdown-option">
              {{ operatorMap[name] }}
            </div>
          </template>
        </select-input>
      </template>
      <div class="d-flex">
        <text-input
          v-if="isNumeric()"
          id="value-numeric"
          size="sm"
          placeholder="New value"
          :value="value"
          type="number"
          @input="changeConditionValue(parseInt($event, 10))"
        />
        <b-dropdown
          v-else-if="!isBoolean()"
          class="value-dropdown mb-2"
          data-testid="condition-value-dropdown"
          :disabled="disabled || !selectedOperator"
          boundary="viewport"
        >
          <template slot="button-content">
            <span
              v-if="!value || (Array.isArray(value) && value.length === 0)"
              class="text-muted"
              >Select a value</span
            >
            <div
              v-else-if="Array.isArray(value)"
              class="d-flex flex-wrap align-content-between i-my-2"
            >
              <b-badge
                v-for="valueOption in value"
                :key="valueOption"
                class="i-m-2"
              >
                {{ resolveOption(valueOption) }}
              </b-badge>
            </div>
            <span v-else>{{ resolveOption(value) }}</span>
          </template>
          <b-dropdown-form
            v-if="
              selectedOperator &&
              selectedOperator.input_type === 'create_multiple'
            "
            id="value-dropdown-create"
            class="px-2 mt-2"
            @submit.prevent="() => createValue()"
          >
            <text-input
              id="value-dropdown-create-multiple"
              size="sm"
              placeholder="New value"
              :value="newValueItem"
              @input="newValueItem = $event"
            />
          </b-dropdown-form>
          <b-dropdown-header
            v-if="isMultiSelect && selectedVariable.options.length > 0"
            id="value-dropdown-options"
          >
            Options
          </b-dropdown-header>
          <b-dropdown-item
            v-if="
              selectedOperator &&
              selectedOperator.input_type === 'create_multiple' &&
              newValueItem
            "
            @click="() => createValue()"
          >
            <span
              >Create value <em>{{ newValueItem }}</em
              >?</span
            >
          </b-dropdown-item>
          <b-dropdown-item
            v-for="option in valueOptions"
            :key="option"
            :active="
              isMultiSelect ? (value || []).includes(option) : option === value
            "
            link-class="d-flex align-items-center"
            data-testid="condition-value-dropdown-option"
            @click.native.prevent.stop="() => changeConditionValue(option)"
          >
            <font-awesome-icon
              v-if="isMultiSelect"
              :icon="[
                'far',
                (value || []).includes(option) ? 'check-square' : 'square',
              ]"
              class="selected-check mr-2"
            />
            <div>{{ resolveOption(option) }}</div>
          </b-dropdown-item>
        </b-dropdown>
        <div class="d-flex align-items-center">
          <button
            v-if="!disabled && canDelete"
            class="btn delete-button icon-button"
            data-testid="delete-condition-button"
            @click="deleteCondition"
          >
            <font-awesome-icon icon="trash" />
          </button>
        </div>
      </div>
    </div>
    <b-badge v-if="!isLast" class="mb-3"> AND </b-badge>
  </div>
</template>

<script>
import { SelectInput, TextInput } from '@inscribe/ui-kit';
import { operatorMap, resolveConditionValue } from '@/utils/conditions';
import RuleEngine from '@/utils/ruleEngine';

export default {
  components: {
    'select-input': SelectInput,
    'text-input': TextInput,
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    disableVariable: {
      type: Boolean,
      default: false,
    },
    condition: {
      type: Object,
      required: true,
    },
    isLast: {
      type: Boolean,
      default: false,
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      newValueItem: '',
      operatorMap,
    };
  },
  computed: {
    value() {
      return this.condition.value || this.condition.function_value;
    },
    selectedVariable() {
      return this.$store.state.config.rulesSchema.variables.find(
        ({ name }) => name === this.condition.name,
      );
    },
    selectedOperator() {
      return this.findOperator(this.condition.operator);
    },
    valueOptions() {
      const { options = [] } = this.selectedVariable || {};
      return this.selectedOperator &&
        this.selectedOperator.input_type === 'create_multiple'
        ? [...(this.condition.value || []), ...options]
        : options;
    },
    variables() {
      return this.$store.state.config.rulesSchema.variables;
    },
    variableOptions() {
      return this.variables.map((variable) => ({
        ...variable,
        text: variable.label,
        value: variable.name,
      }));
    },
    variableOperators() {
      return this.$store.state.config.rulesSchema.variable_type_operators;
    },
    availableOperators() {
      if (!this.selectedVariable) {
        return null;
      }

      return this.variableOperators[this.selectedVariable.field_type].map(
        (operator) => ({
          ...operator,
          text: operator.label,
          value: operator.name,
        }),
      );
    },
    valueInputType() {
      if (!this.selectedOperator) {
        return null;
      }

      return this.selectedOperator.input_type;
    },
    isMultiSelect() {
      return (
        this.valueInputType === 'select_multiple' ||
        this.valueInputType === 'create_multiple'
      );
    },
  },
  methods: {
    hasFunctionValue(variable) {
      return variable && variable.field_type === 'select_multiple';
    },
    createValue() {
      if (this.newValueItem) {
        this.updateRuleCondition({
          name: this.condition.name,
          operator: this.condition.operator,
          value: [...(this.condition.value || []), this.newValueItem.trim()],
        });

        this.newValueItem = '';
      }
    },
    getConditionValue(variable, operator, value) {
      const shouldValueBeArray =
        operator &&
        ['select_multiple', 'create_multiple'].includes(operator.input_type);
      const isValueArray = Array.isArray(value);

      if (shouldValueBeArray) {
        if (!value || isValueArray) {
          return value;
        }
        return [value];
      }

      if (isValueArray && value && value.length > 0) {
        return value[0];
      }

      return value;
    },
    changeConditionVariable(variableName) {
      this.updateRuleCondition({
        name: variableName,
        operator: null,
        value: this.getConditionValue(variableName, null, null),
      });
    },
    changeConditionOperator(operator) {
      const operatorDef = this.findOperator(operator);
      this.updateRuleCondition({
        name: this.condition.name,
        operator: operator,
        value: this.getConditionValue(
          this.selectedVariable,
          operatorDef,
          this.value,
        ),
      });
    },
    changeConditionValue(option) {
      const valueAr = this.condition.value || [];
      const newOption =
        this.hasFunctionValue(this.selectedVariable) || this.isMultiSelect
          ? valueAr.includes(option)
            ? valueAr.filter((val) => val !== option)
            : [...valueAr, option]
          : option;

      this.updateRuleCondition({
        name: this.condition.name,
        operator: this.condition.operator,
        value: newOption,
      });
    },
    updateRuleCondition(condition) {
      this.$emit('update', condition);
    },
    findOperator(operatorName) {
      if (!this.availableOperators) {
        return null;
      }

      return this.availableOperators.find(({ name }) => name === operatorName);
    },
    resolveOption(option) {
      return resolveConditionValue(
        this.selectedVariable && this.selectedVariable.name,
        option,
      );
    },
    deleteCondition() {
      this.$emit('delete');
    },
    isNumeric() {
      return new RuleEngine(this.$store.state.config.rulesSchema).isNumeric(
        this.selectedVariable,
      );
    },
    isBoolean() {
      return new RuleEngine(this.$store.state.config.rulesSchema).isBoolean(
        this.selectedVariable,
      );
    },
  },
};
</script>

<style lang="scss">
.condition {
  .delete-button {
    color: $color-navy-blue-40;
    height: 28px;
    visibility: hidden;
  }

  &:hover .delete-button {
    visibility: visible;
  }

  .btn.disabled,
  .btn:disabled {
    opacity: 0.95 !important;
    background-color: #f4f4f4 !important;
  }
  .disabled-variable-text {
    font-size: 14px;
    color: #6c757d;
  }

  .value-dropdown {
    .btn {
      padding: 0 32px 0 12px;
      .badge {
        top: 0;
        margin: 0px 4px 4px 0;
      }
    }
  }
}
</style>
